import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import Header from '../components/Header';
import {
  ChevronRight,
  ChevronLeft,
  Upload,
  X,
  CheckCircle2,
  User,
  Briefcase,
  Car,
  Camera,
  FileText,
  Loader2,
  Phone,
  Mail,
  MapPin,
  Clock,
  Award,
  Shield,
  Star
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Service options
const serviceOptions = [
  { id: 'handyman', label: 'Handyman / General Repairs' },
  { id: 'carpentry', label: 'Carpentry & Joinery' },
  { id: 'painting', label: 'Painting & Decorating' },
  { id: 'plastering', label: 'Plastering & Rendering' },
  { id: 'plumbing', label: 'Plumbing' },
  { id: 'electrical', label: 'Electrical' },
  { id: 'tiling', label: 'Tiling' },
  { id: 'flooring', label: 'Flooring' },
  { id: 'kitchen_fitting', label: 'Kitchen Fitting' },
  { id: 'bathroom_fitting', label: 'Bathroom Fitting' },
  { id: 'locksmith', label: 'Locksmith' },
  { id: 'cleaning', label: 'End of Tenancy Cleaning' }
];

// Coverage areas
const coverageAreas = [
  'Manchester City Centre',
  'Stockport',
  'Cheadle',
  'Didsbury',
  'Wilmslow',
  'Altrincham',
  'Sale',
  'Trafford',
  'Salford',
  'Bolton',
  'Bury',
  'Rochdale',
  'Oldham',
  'Tameside',
  'Wigan'
];

// Availability days
const availabilityDays = [
  { id: 'monday', label: 'Mon' },
  { id: 'tuesday', label: 'Tue' },
  { id: 'wednesday', label: 'Wed' },
  { id: 'thursday', label: 'Thu' },
  { id: 'friday', label: 'Fri' },
  { id: 'saturday', label: 'Sat' },
  { id: 'sunday', label: 'Sun' }
];

// Step indicator component
const StepIndicator = ({ currentStep, totalSteps }) => (
  <div className="flex items-center justify-center mb-8">
    {/* Index as key is acceptable here - fixed-length step sequence that never reorders */}
    {[...Array(totalSteps)].map((_, idx) => (
      <React.Fragment key={`step-${idx + 1}`}>
        <div
          className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-all ${
            idx + 1 <= currentStep
              ? 'bg-brand-yellow text-brand-navy'
              : 'bg-slate-200 text-brand-slate/80'
          }`}
        >
          {idx + 1 < currentStep ? (
            <CheckCircle2 className="w-5 h-5" />
          ) : (
            idx + 1
          )}
        </div>
        {idx < totalSteps - 1 && (
          <div
            className={`w-12 h-1 mx-1 transition-all ${
              idx + 1 < currentStep ? 'bg-brand-yellow' : 'bg-slate-200'
            }`}
          />
        )}
      </React.Fragment>
    ))}
  </div>
);

// Skill rating slider component
const SkillRating = ({ label, value, onChange }) => (
  <div className="space-y-2">
    <div className="flex justify-between items-center">
      <Label className="text-sm font-medium text-brand-slate">{label}</Label>
      <span className="text-sm font-semibold text-brand-yellow">
        {value}/10
      </span>
    </div>
    <Slider
      value={[value]}
      onValueChange={(vals) => onChange(vals[0])}
      max={10}
      min={1}
      step={1}
      className="w-full"
    />
    <div className="flex justify-between text-xs text-slate-400">
      <span>Beginner</span>
      <span>Expert</span>
    </div>
  </div>
);

// File upload component
const FileUploadZone = ({ label, accept, multiple, files, onFilesChange, maxFiles = 5, icon: Icon }) => {
  const [isDragging, setIsDragging] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles = droppedFiles.filter(f => {
      const ext = f.name.split('.').pop().toLowerCase();
      return accept.includes(ext) || accept.includes(f.type);
    });
    if (multiple) {
      onFilesChange([...files, ...validFiles].slice(0, maxFiles));
    } else {
      onFilesChange(validFiles.slice(0, 1));
    }
  }, [files, onFilesChange, accept, multiple, maxFiles]);

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    if (multiple) {
      onFilesChange([...files, ...selectedFiles].slice(0, maxFiles));
    } else {
      onFilesChange(selectedFiles.slice(0, 1));
    }
  };

  const removeFile = (index) => {
    onFilesChange(files.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium text-brand-slate">{label}</Label>
      <div
        className={`border-2 border-dashed rounded-xl p-6 text-center transition-all cursor-pointer ${
          isDragging
            ? 'border-brand-yellow bg-brand-grey'
            : 'border-slate-300 hover:border-brand-yellow hover:bg-brand-grey'
        }`}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
        onClick={() => document.getElementById(`file-input-${label.replace(/\s/g, '')}`).click()}
      >
        <input
          id={`file-input-${label.replace(/\s/g, '')}`}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={handleFileInput}
          className="hidden"
        />
        <Icon className="w-10 h-10 mx-auto text-slate-400 mb-3" />
        <p className="text-sm text-brand-slate">
          Drag & drop or <span className="text-brand-yellow font-medium">browse</span>
        </p>
        <p className="text-xs text-slate-400 mt-1">
          {multiple ? `Up to ${maxFiles} files` : 'Single file'}
        </p>
      </div>
      
      {files.length > 0 && (
        <div className="space-y-2">
          {files.map((file) => (
            <div key={file.name} className="flex items-center justify-between bg-slate-100 rounded-lg p-2 pr-3">
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 text-brand-slate/80 flex-shrink-0" />
                <span className="text-sm text-brand-slate truncate">{file.name}</span>
              </div>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeFile(files.indexOf(file)); }}
                className="text-slate-400 hover:text-red-500 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const JoinNetwork = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    // Step 1: Personal Details
    name: '',
    email: '',
    phone: '',
    postcode: '',
    
    // Step 2: Services & Skills
    services: [],
    experienceYears: 3,
    coverageAreas: [],
    rightToWork: false,
    
    // Step 3: Skill Ratings
    skillRatings: {
      quality: 7,
      speed: 7,
      communication: 7,
      problemSolving: 7,
      reliability: 8
    },
    preferredWork: [],
    
    // Step 4: Transport
    hasDrivingLicence: false,
    hasVehicle: false,
    vehicleType: '',
    canCarryEquipment: false,
    
    // Step 5: Portfolio & Docs
    cvFile: [],
    workPhotos: [],
    certifications: '',
    insuranceDetails: '',
    
    // Step 6: Availability & Goals
    hoursPerWeek: 20,
    availabilityDays: [],
    goals: '',
    bio: ''
  });

  const totalSteps = 6;

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateSkillRating = (skill, value) => {
    setFormData(prev => ({
      ...prev,
      skillRatings: { ...prev.skillRatings, [skill]: value }
    }));
  };

  const toggleArrayItem = (field, item) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].includes(item)
        ? prev[field].filter(i => i !== item)
        : [...prev[field], item]
    }));
  };

  const validateStep = () => {
    switch (currentStep) {
      case 1:
        return formData.name && formData.email && formData.phone && formData.postcode;
      case 2:
        return formData.services.length > 0 && formData.coverageAreas.length > 0 && formData.rightToWork;
      case 3:
        return true; // Skill ratings have defaults
      case 4:
        return true; // Transport is optional
      case 5:
        return true; // Portfolio is optional
      case 6:
        return formData.availabilityDays.length > 0;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep() && currentStep < totalSteps) {
      setCurrentStep(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const uploadFile = async (file, type) => {
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    
    const endpoint = type === 'cv' ? '/api/upload-document' : '/api/upload';
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: 'POST',
      body: formDataUpload
    });
    
    if (!response.ok) throw new Error('File upload failed');
    return response.json();
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setIsSubmitting(true);
    setSubmitError('');
    
    try {
      // Upload files first
      let cvUrl = null;
      let workPhotoUrls = [];
      
      if (formData.cvFile.length > 0) {
        const cvResult = await uploadFile(formData.cvFile[0], 'cv');
        cvUrl = cvResult.path;
      }
      
      for (const photo of formData.workPhotos) {
        const photoResult = await uploadFile(photo, 'photo');
        workPhotoUrls.push(photoResult.path);
      }
      
      // Submit application
      const applicationData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        postcode: formData.postcode,
        services: formData.services,
        experience_years: formData.experienceYears,
        coverage_areas: formData.coverageAreas,
        right_to_work: formData.rightToWork,
        skill_ratings: formData.skillRatings,
        preferred_work: formData.preferredWork,
        has_driving_licence: formData.hasDrivingLicence,
        has_vehicle: formData.hasVehicle,
        vehicle_type: formData.vehicleType,
        can_carry_equipment: formData.canCarryEquipment,
        cv_url: cvUrl,
        work_photos: workPhotoUrls,
        certifications: formData.certifications,
        insurance_details: formData.insuranceDetails,
        hours_per_week: formData.hoursPerWeek,
        availability_days: formData.availabilityDays,
        goals: formData.goals,
        bio: formData.bio
      };
      
      const response = await fetch(`${API_URL}/api/tradesperson/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(applicationData)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Submission failed');
      }
      
      navigate('/join/success');
    } catch (error) {
      setSubmitError(error.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step content components
  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-brand-grey rounded-full flex items-center justify-center mx-auto mb-4">
          <User className="w-8 h-8 text-brand-yellow" />
        </div>
        <h2 className="text-2xl font-bold text-brand-navy">Your Details</h2>
        <p className="text-brand-slate mt-2">Tell us a bit about yourself</p>
      </div>
      
      <div className="space-y-4">
        <div>
          <Label htmlFor="name">Full Name *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            placeholder="John Smith"
            className="mt-1"
            data-testid="input-name"
          />
        </div>
        
        <div>
          <Label htmlFor="email">Email Address *</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => updateField('email', e.target.value)}
            placeholder="john@example.com"
            className="mt-1"
            data-testid="input-email"
          />
        </div>
        
        <div>
          <Label htmlFor="phone">Phone Number *</Label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => updateField('phone', e.target.value)}
            placeholder="07700 900123"
            className="mt-1"
            data-testid="input-phone"
          />
        </div>
        
        <div>
          <Label htmlFor="postcode">Postcode *</Label>
          <Input
            id="postcode"
            value={formData.postcode}
            onChange={(e) => updateField('postcode', e.target.value.toUpperCase())}
            placeholder="M1 1AA"
            className="mt-1"
            data-testid="input-postcode"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-brand-grey rounded-full flex items-center justify-center mx-auto mb-4">
          <Briefcase className="w-8 h-8 text-brand-yellow" />
        </div>
        <h2 className="text-2xl font-bold text-brand-navy">Services & Experience</h2>
        <p className="text-brand-slate mt-2">What services do you offer?</p>
      </div>
      
      <div className="space-y-6">
        <div>
          <Label className="text-base font-semibold mb-3 block">Select Your Services *</Label>
          <div className="grid grid-cols-2 gap-3">
            {serviceOptions.map((service) => (
              <div
                key={service.id}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.services.includes(service.id)
                    ? 'border-brand-yellow bg-brand-grey'
                    : 'border-slate-200 hover:border-yellow-300'
                }`}
                onClick={() => toggleArrayItem('services', service.id)}
                data-testid={`service-${service.id}`}
              >
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={formData.services.includes(service.id)}
                    className="pointer-events-none"
                  />
                  <span className="text-sm font-medium text-brand-slate">{service.label}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        <div>
          <Label className="text-base font-semibold mb-3 block">
            Years of Experience: <span className="text-brand-yellow">{formData.experienceYears} years</span>
          </Label>
          <Slider
            value={[formData.experienceYears]}
            onValueChange={(vals) => updateField('experienceYears', vals[0])}
            max={30}
            min={1}
            step={1}
            className="w-full"
            data-testid="slider-experience"
          />
          <div className="flex justify-between text-xs text-slate-400 mt-1">
            <span>1 year</span>
            <span>30+ years</span>
          </div>
        </div>
        
        <div>
          <Label className="text-base font-semibold mb-3 block">Coverage Areas *</Label>
          <div className="grid grid-cols-3 gap-2">
            {coverageAreas.map((area) => (
              <div
                key={area}
                className={`p-2 rounded-lg border text-center cursor-pointer transition-all text-sm ${
                  formData.coverageAreas.includes(area)
                    ? 'border-brand-yellow bg-brand-grey text-brand-navy'
                    : 'border-slate-200 hover:border-yellow-300 text-brand-slate'
                }`}
                onClick={() => toggleArrayItem('coverageAreas', area)}
                data-testid={`area-${area.replace(/\s/g, '-')}`}
              >
                {area}
              </div>
            ))}
          </div>
        </div>
        
        <div className="p-4 bg-brand-grey rounded-xl">
          <div
            className="flex items-center gap-3 cursor-pointer"
            onClick={() => updateField('rightToWork', !formData.rightToWork)}
          >
            <Checkbox
              checked={formData.rightToWork}
              className="pointer-events-none"
              data-testid="checkbox-right-to-work"
            />
            <div>
              <Label className="text-sm font-medium text-brand-slate cursor-pointer">
                I confirm I have the right to work in the UK *
              </Label>
              <p className="text-xs text-brand-slate/80 mt-1">
                You'll need to provide proof of eligibility if your application is successful
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-brand-grey rounded-full flex items-center justify-center mx-auto mb-4">
          <Star className="w-8 h-8 text-brand-yellow" />
        </div>
        <h2 className="text-2xl font-bold text-brand-navy">Rate Your Skills</h2>
        <p className="text-brand-slate mt-2">Be honest - we value accuracy over exaggeration</p>
      </div>
      
      <div className="space-y-6">
        <SkillRating
          label="Quality of Work"
          value={formData.skillRatings.quality}
          onChange={(v) => updateSkillRating('quality', v)}
        />
        <SkillRating
          label="Speed & Efficiency"
          value={formData.skillRatings.speed}
          onChange={(v) => updateSkillRating('speed', v)}
        />
        <SkillRating
          label="Communication Skills"
          value={formData.skillRatings.communication}
          onChange={(v) => updateSkillRating('communication', v)}
        />
        <SkillRating
          label="Problem Solving"
          value={formData.skillRatings.problemSolving}
          onChange={(v) => updateSkillRating('problemSolving', v)}
        />
        <SkillRating
          label="Reliability & Punctuality"
          value={formData.skillRatings.reliability}
          onChange={(v) => updateSkillRating('reliability', v)}
        />
      </div>
      
      <div className="mt-8">
        <Label className="text-base font-semibold mb-3 block">Preferred Work Type</Label>
        <div className="grid grid-cols-2 gap-3">
          {['Small repairs', 'Large projects', 'Emergency callouts', 'Regular maintenance'].map((type) => (
            <div
              key={type}
              className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                formData.preferredWork.includes(type)
                  ? 'border-brand-yellow bg-brand-grey'
                  : 'border-slate-200 hover:border-yellow-300'
              }`}
              onClick={() => toggleArrayItem('preferredWork', type)}
            >
              <div className="flex items-center gap-2">
                <Checkbox checked={formData.preferredWork.includes(type)} className="pointer-events-none" />
                <span className="text-sm font-medium text-brand-slate">{type}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-brand-grey rounded-full flex items-center justify-center mx-auto mb-4">
          <Car className="w-8 h-8 text-brand-yellow" />
        </div>
        <h2 className="text-2xl font-bold text-brand-navy">Transport & Logistics</h2>
        <p className="text-brand-slate mt-2">Help us understand your mobility</p>
      </div>
      
      <div className="space-y-4">
        <div
          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
            formData.hasDrivingLicence ? 'border-brand-yellow bg-brand-grey' : 'border-slate-200 hover:border-yellow-300'
          }`}
          onClick={() => updateField('hasDrivingLicence', !formData.hasDrivingLicence)}
          data-testid="toggle-driving-licence"
        >
          <div className="flex items-center gap-3">
            <Checkbox checked={formData.hasDrivingLicence} className="pointer-events-none" />
            <div>
              <Label className="text-sm font-medium text-brand-slate cursor-pointer">
                I have a valid UK driving licence
              </Label>
            </div>
          </div>
        </div>
        
        <div
          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
            formData.hasVehicle ? 'border-brand-yellow bg-brand-grey' : 'border-slate-200 hover:border-yellow-300'
          }`}
          onClick={() => updateField('hasVehicle', !formData.hasVehicle)}
          data-testid="toggle-vehicle"
        >
          <div className="flex items-center gap-3">
            <Checkbox checked={formData.hasVehicle} className="pointer-events-none" />
            <div>
              <Label className="text-sm font-medium text-brand-slate cursor-pointer">
                I have my own vehicle
              </Label>
            </div>
          </div>
        </div>
        
        {formData.hasVehicle && (
          <div className="pl-4 border-l-2 border-yellow-300">
            <Label htmlFor="vehicleType">Vehicle Type</Label>
            <Input
              id="vehicleType"
              value={formData.vehicleType}
              onChange={(e) => updateField('vehicleType', e.target.value)}
              placeholder="e.g., Van, Estate car, Pickup truck"
              className="mt-1"
              data-testid="input-vehicle-type"
            />
          </div>
        )}
        
        <div
          className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
            formData.canCarryEquipment ? 'border-brand-yellow bg-brand-grey' : 'border-slate-200 hover:border-yellow-300'
          }`}
          onClick={() => updateField('canCarryEquipment', !formData.canCarryEquipment)}
          data-testid="toggle-equipment"
        >
          <div className="flex items-center gap-3">
            <Checkbox checked={formData.canCarryEquipment} className="pointer-events-none" />
            <div>
              <Label className="text-sm font-medium text-brand-slate cursor-pointer">
                I can carry tools and materials
              </Label>
              <p className="text-xs text-brand-slate/80 mt-1">
                Including ladders, power tools, building materials etc.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-brand-grey rounded-full flex items-center justify-center mx-auto mb-4">
          <Camera className="w-8 h-8 text-brand-yellow" />
        </div>
        <h2 className="text-2xl font-bold text-brand-navy">Portfolio & Documents</h2>
        <p className="text-brand-slate mt-2">Show us your best work (optional but recommended)</p>
      </div>
      
      <div className="space-y-6">
        <FileUploadZone
          label="Upload CV (PDF or Word)"
          accept=".pdf,.doc,.docx"
          multiple={false}
          files={formData.cvFile}
          onFilesChange={(files) => updateField('cvFile', files)}
          maxFiles={1}
          icon={FileText}
        />
        
        <FileUploadZone
          label="Work Photos (up to 5 images)"
          accept=".jpg,.jpeg,.png,.webp"
          multiple={true}
          files={formData.workPhotos}
          onFilesChange={(files) => updateField('workPhotos', files)}
          maxFiles={5}
          icon={Camera}
        />
        
        <div>
          <Label htmlFor="certifications">Certifications & Qualifications</Label>
          <Textarea
            id="certifications"
            value={formData.certifications}
            onChange={(e) => updateField('certifications', e.target.value)}
            placeholder="List any relevant certifications, NVQs, City & Guilds, etc."
            className="mt-1"
            rows={3}
            data-testid="textarea-certifications"
          />
        </div>
        
        <div>
          <Label htmlFor="insurance">Insurance Details</Label>
          <Textarea
            id="insurance"
            value={formData.insuranceDetails}
            onChange={(e) => updateField('insuranceDetails', e.target.value)}
            placeholder="Public liability insurance, professional indemnity, etc."
            className="mt-1"
            rows={3}
            data-testid="textarea-insurance"
          />
        </div>
      </div>
    </div>
  );

  const renderStep6 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-brand-grey rounded-full flex items-center justify-center mx-auto mb-4">
          <Clock className="w-8 h-8 text-brand-yellow" />
        </div>
        <h2 className="text-2xl font-bold text-brand-navy">Availability & Goals</h2>
        <p className="text-brand-slate mt-2">When can you work and what are you looking for?</p>
      </div>
      
      <div className="space-y-6">
        <div>
          <Label className="text-base font-semibold mb-3 block">
            Hours Available Per Week: <span className="text-brand-yellow">{formData.hoursPerWeek} hours</span>
          </Label>
          <Slider
            value={[formData.hoursPerWeek]}
            onValueChange={(vals) => updateField('hoursPerWeek', vals[0])}
            max={60}
            min={5}
            step={5}
            className="w-full"
            data-testid="slider-hours"
          />
          <div className="flex justify-between text-xs text-slate-400 mt-1">
            <span>5 hours</span>
            <span>60+ hours</span>
          </div>
        </div>
        
        <div>
          <Label className="text-base font-semibold mb-3 block">Available Days *</Label>
          <div className="flex flex-wrap gap-2">
            {availabilityDays.map((day) => (
              <button
                key={day.id}
                type="button"
                className={`px-4 py-2 rounded-lg border-2 font-medium transition-all ${
                  formData.availabilityDays.includes(day.id)
                    ? 'border-brand-yellow bg-brand-yellow text-brand-navy'
                    : 'border-slate-200 text-brand-slate hover:border-yellow-300'
                }`}
                onClick={() => toggleArrayItem('availabilityDays', day.id)}
                data-testid={`day-${day.id}`}
              >
                {day.label}
              </button>
            ))}
          </div>
        </div>
        
        <div>
          <Label htmlFor="goals">What are you looking for?</Label>
          <Textarea
            id="goals"
            value={formData.goals}
            onChange={(e) => updateField('goals', e.target.value)}
            placeholder="Tell us what you're hoping to achieve by joining our network..."
            className="mt-1"
            rows={3}
            data-testid="textarea-goals"
          />
        </div>
        
        <div>
          <Label htmlFor="bio">Short Bio</Label>
          <Textarea
            id="bio"
            value={formData.bio}
            onChange={(e) => updateField('bio', e.target.value)}
            placeholder="A brief introduction about yourself and your work experience..."
            className="mt-1"
            rows={4}
            data-testid="textarea-bio"
          />
        </div>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return renderStep3();
      case 4: return renderStep4();
      case 5: return renderStep5();
      case 6: return renderStep6();
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Header variant="default" />
      
      {/* Hero Banner */}
      <section className="pt-24 pb-8 lg:pt-32 lg:pb-12 bg-brand-navy text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Join Our Network of{' '}
            <span className="text-brand-yellow">Trusted Tradespeople</span>
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto">
            We're always looking for skilled, reliable tradespeople to join our growing team. 
            Complete this application and we'll be in touch.
          </p>
        </div>
      </section>
      
      {/* Benefits */}
      <section className="py-8 bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Clock, text: 'Flexible Hours' },
              { icon: Award, text: 'Fair Pay' },
              { icon: Shield, text: 'Insured Jobs' },
              { icon: Star, text: 'Build Reputation' }
            ].map((benefit) => (
              <div key={benefit.text} className="flex items-center gap-2 text-sm text-brand-slate">
                <benefit.icon className="w-5 h-5 text-brand-yellow flex-shrink-0" />
                <span>{benefit.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Form */}
      <section className="py-12">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="shadow-xl border-0">
            <CardContent className="p-6 sm:p-8">
              <StepIndicator currentStep={currentStep} totalSteps={totalSteps} />
              
              {renderCurrentStep()}
              
              {submitError && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                  {submitError}
                </div>
              )}
              
              {/* Navigation */}
              <div className="flex justify-between mt-8 pt-6 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className="gap-2"
                  data-testid="btn-prev"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </Button>
                
                {currentStep < totalSteps ? (
                  <Button
                    type="button"
                    onClick={nextStep}
                    disabled={!validateStep()}
                    className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-semibold gap-2"
                    data-testid="btn-next"
                  >
                    Continue
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                ) : (
                  <Button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!validateStep() || isSubmitting}
                    className="bg-brand-yellow hover:bg-yellow-300 text-brand-navy font-semibold gap-2"
                    data-testid="btn-submit"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Application
                        <CheckCircle2 className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default JoinNetwork;
